import { SnapshotChangePill } from '@/components/snapshot/SnapshotChangePill';
import type { CSSProperties } from 'react';
import {
  formatCurrencyValue,
  formatNumberValue,
  formatPercentValue,
} from '@/api/keywordMetrics';
import {
  ensureDerivedMetrics,
  WORKSPACE_METRIC_COLUMNS,
  type MetricPolarity,
  type WorkspaceMetricColumnDef,
} from '@/utils/workspaceAdProductMetrics';
import { cn } from '@/utils/cn';
import { workspaceTableClass } from '@/components/workspace/WorkspaceSection';
import { WorkspaceColumnHeader } from '@/components/workspace/WorkspaceColumnHeader';
import type { ColumnDropPosition } from '@/components/workspace/useColumnOrder';
import { WorkspaceColumnInsertIndicator } from '@/components/workspace/WorkspaceColumnInsertIndicator';
import {
  workspaceColumnStickyProps,
  workspaceStickyCheckboxFooterClass,
  workspaceStickyFooterFillClass,
} from '@/components/workspace/workspaceStickyColumns';

/**
 * Metric column sizing for `table-layout: fixed` + `<colgroup>` resize.
 * `w-0 min-w-0` lets col widths drive the cell; padding stays in-box via `box-border`.
 */
export const WORKSPACE_METRIC_COL_CLASS =
  'box-border w-0 min-w-0 max-w-full px-1';

export function formatWorkspaceMetricValue(
  def: WorkspaceMetricColumnDef,
  value: number | null
): string {
  if (value === null) return '—';
  switch (def.format) {
    case 'int':
      return formatNumberValue(value, 0);
    case 'currency':
      return formatCurrencyValue(value);
    case 'percent':
      return formatPercentValue(value);
    case 'ratio':
      return formatNumberValue(value, 2);
    default:
      return formatNumberValue(value, 2);
  }
}

export function WorkspaceMetricCell({
  def,
  current,
  previous,
  compareEnabled,
  isLoading,
  className,
  style,
}: {
  def: WorkspaceMetricColumnDef;
  current: number | null;
  previous: number | null;
  compareEnabled: boolean;
  isLoading?: boolean;
  className?: string;
  style?: CSSProperties;
}) {
  if (isLoading) {
    return (
      <td className={cn(WORKSPACE_METRIC_COL_CLASS, 'overflow-hidden py-2 text-right', className)} style={style}>
        <span className="ml-auto block h-3.5 w-12 animate-pulse rounded bg-surface-muted" />
      </td>
    );
  }

  const cur = current ?? 0;
  const prev = previous ?? 0;

  return (
    <td className={cn(WORKSPACE_METRIC_COL_CLASS, 'overflow-hidden py-2 text-right', className)} style={style}>
      <div className="flex w-full min-w-0 max-w-full flex-col items-end leading-none">
        <div className="w-full min-w-0 truncate text-right font-mono text-[11px] font-normal leading-tight tabular-nums text-foreground">
          {formatWorkspaceMetricValue(def, current)}
        </div>
        {compareEnabled ? (
          <div className="mt-px flex w-full min-w-0 max-w-full items-center justify-end gap-3 overflow-hidden">
            <span className="min-w-0 truncate font-mono text-[10px] font-light leading-tight tabular-nums text-foreground-muted">
              {formatWorkspaceMetricValue(def, previous)}
            </span>
            <span className="shrink-0">
              <SnapshotChangePill
                current={cur}
                previous={prev}
                polarity={def.polarity as MetricPolarity}
                show
                variant="compact"
              />
            </span>
          </div>
        ) : null}
      </div>
    </td>
  );
}

export function WorkspaceMetricHeaderCells({
  columns = WORKSPACE_METRIC_COLUMNS,
  sortBy,
  sortDir,
  onSort,
  onFreeze,
  frozenColumnIds,
  pinnedFrozenColumnId,
  frozenLeftById = {},
  movable = false,
  sortable = false,
  onReorderBefore,
  onDragStartColumn,
  onDragHoverColumn,
  onDropColumn,
  onDragEndColumn,
  activeDragColumnId,
  hoverColumnId,
  hoverPosition,
  hoverInsertIndex,
}: {
  columns?: WorkspaceMetricColumnDef[];
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
  onSort?: (columnId: string, dir?: 'asc' | 'desc') => void;
  onFreeze?: (columnId: string) => void;
  frozenColumnIds?: readonly string[];
  pinnedFrozenColumnId?: string | null;
  frozenLeftById?: Record<string, number>;
  movable?: boolean;
  sortable?: boolean;
  onReorderBefore?: (draggedId: string, targetId: string) => void;
  onDragStartColumn?: (columnId: string) => void;
  onDragHoverColumn?: (
    targetId: string,
    position: ColumnDropPosition,
    clientX?: number,
    insertIndex?: number
  ) => void;
  onDropColumn?: (targetId: string, position: ColumnDropPosition) => void;
  onDragEndColumn?: () => void;
  activeDragColumnId?: string | null;
  hoverColumnId?: string | null;
  hoverPosition?: ColumnDropPosition | null;
  hoverInsertIndex?: number | null;
} = {}) {
  if (!sortable && !movable) {
    return (
      <>
        {columns.map((def) => (
          <th
            key={def.key}
            className={cn(workspaceTableClass.th, WORKSPACE_METRIC_COL_CLASS, 'text-center')}
          >
            {def.label}
          </th>
        ))}
      </>
    );
  }

  const pinnedId = pinnedFrozenColumnId ?? frozenColumnIds?.[0] ?? null;

  return (
    <>
      {columns.map((def) => {
        const sticky = workspaceColumnStickyProps(def.key, frozenColumnIds, frozenLeftById, 'header');
        return (
          <WorkspaceColumnHeader
            key={def.key}
            label={def.label}
            columnId={def.key}
            sortBy={sortBy}
            sortDir={sortDir}
            onSort={onSort}
            onFreeze={def.key === pinnedId ? undefined : onFreeze}
            isFrozen={def.key !== pinnedId && Boolean(frozenColumnIds?.includes(def.key))}
            onDragStartColumn={onDragStartColumn}
            onDragHoverColumn={(targetId, position, clientX, insertIndex) => {
              if (onDragHoverColumn) {
                onDragHoverColumn(targetId, position, clientX, insertIndex);
                return;
              }
              onReorderBefore?.(activeDragColumnId ?? '', targetId);
            }}
            onDropColumn={onDropColumn}
            onDragEndColumn={onDragEndColumn}
            activeDragColumnId={activeDragColumnId}
            hoverInsertIndex={hoverInsertIndex}
            isDragged={activeDragColumnId === def.key}
            isDropTarget={hoverColumnId === def.key && activeDragColumnId !== def.key}
            dropPosition={hoverPosition ?? null}
            sortable={sortable}
            movable={movable}
            className={cn(sticky.className, WORKSPACE_METRIC_COL_CLASS, 'bg-surface-muted py-2')}
            style={sticky.style}
          />
        );
      })}
      <WorkspaceColumnInsertIndicator
        activeDragColumnId={activeDragColumnId}
        hoverColumnId={hoverColumnId}
        hoverPosition={hoverPosition}
      />
    </>
  );
}

export function WorkspaceMetricDataCells({
  metrics,
  previousMetrics,
  compareEnabled,
  isLoading,
  columns = WORKSPACE_METRIC_COLUMNS,
}: {
  metrics: Record<string, number> | null;
  previousMetrics: Record<string, number> | null;
  compareEnabled: boolean;
  isLoading?: boolean;
  columns?: WorkspaceMetricColumnDef[];
}) {
  const current = {
    ...(metrics ?? {}),
    ...ensureDerivedMetrics(metrics),
  } as Record<string, number>;
  const previous = {
    ...(previousMetrics ?? {}),
    ...ensureDerivedMetrics(previousMetrics),
  } as Record<string, number>;
  return (
    <>
      {columns.map((def) => (
        <WorkspaceMetricCell
          key={def.key}
          def={def}
          current={current[def.key] ?? null}
          previous={previous[def.key] ?? null}
          compareEnabled={compareEnabled}
          isLoading={isLoading}
        />
      ))}
    </>
  );
}

/** Totals footer: leading label cell(s) + metric cells with optional compare. */
export function WorkspaceMetricTotalsRow({
  labelColSpan,
  trailingColSpan = 0,
  current,
  previous,
  compareEnabled,
  isLoading,
  columns = WORKSPACE_METRIC_COLUMNS,
  frozenColumnIds,
  frozenLeftById = {},
  labelColumnId = 'label',
}: {
  labelColSpan: number;
  trailingColSpan?: number;
  current: Record<string, number> | null;
  previous: Record<string, number> | null;
  compareEnabled: boolean;
  isLoading?: boolean;
  columns?: WorkspaceMetricColumnDef[];
  frozenColumnIds?: readonly string[];
  frozenLeftById?: Record<string, number>;
  labelColumnId?: string;
}) {
  const derivedCurrent = {
    ...(current ?? {}),
    ...ensureDerivedMetrics(current),
  } as Record<string, number>;
  const derivedPrevious = {
    ...(previous ?? {}),
    ...ensureDerivedMetrics(previous),
  } as Record<string, number>;
  const labelSticky = workspaceColumnStickyProps(
    labelColumnId,
    frozenColumnIds,
    frozenLeftById,
    'footer'
  );
  return (
    <tr className="border-t-2 border-border bg-surface-muted/60">
      {labelColSpan >= 2 ? (
        <>
          <td className={workspaceStickyCheckboxFooterClass()} />
          <td
            className={`${workspaceTableClass.td} ${labelSticky.className} text-left text-[11px] font-bold text-foreground`}
            style={labelSticky.style}
          >
            Total
          </td>
          {labelColSpan > 2
            ? Array.from({ length: labelColSpan - 2 }, (_, i) => (
                <td
                  key={`total-pad-${i}`}
                  className={`${workspaceTableClass.td} ${workspaceStickyFooterFillClass()}`}
                />
              ))
            : null}
        </>
      ) : (
        <td
          className={`${workspaceTableClass.td} ${labelSticky.className} text-left text-[11px] font-bold text-foreground`}
          style={labelSticky.style}
          colSpan={labelColSpan}
        >
          Total
        </td>
      )}
      {columns.map((def) => {
        const sticky = workspaceColumnStickyProps(def.key, frozenColumnIds, frozenLeftById, 'footer');
        return (
          <WorkspaceMetricCell
            key={def.key}
            def={def}
            current={derivedCurrent[def.key] ?? null}
            previous={derivedPrevious[def.key] ?? null}
            compareEnabled={compareEnabled}
            isLoading={isLoading}
            className={sticky.className}
            style={sticky.style}
          />
        );
      })}
      {trailingColSpan > 0 ? (
        <td
          className={`${workspaceTableClass.td} ${workspaceStickyFooterFillClass()}`}
          colSpan={trailingColSpan}
        />
      ) : null}
    </tr>
  );
}
